import * as THREE from 'three';

export type ImageKind = 'data' | 'color' | 'sprite';

/**
 * Fetches and decodes an image without colour management or premultiplication, so encoded
 * data (HDR mantissas, surface ids) reaches the GPU bit-exact.
 */
export async function loadBitmap(
  url: string,
  kind: ImageKind,
  signal?: AbortSignal,
): Promise<ImageBitmap> {
  const response = await fetch(url, { signal, credentials: 'omit' });
  if (!response.ok) throw new Error(`Failed to load ${url}: HTTP ${response.status}`);
  const blob = await response.blob();
  return createImageBitmap(blob, {
    colorSpaceConversion: 'none',
    premultiplyAlpha: kind === 'sprite' ? 'premultiply' : 'none',
    imageOrientation: 'none',
  });
}

export function dataTexture(bitmap: ImageBitmap, filter: 'nearest' | 'linear'): THREE.Texture {
  const texture = new THREE.Texture(bitmap);
  texture.flipY = false;
  texture.colorSpace = THREE.NoColorSpace;
  texture.generateMipmaps = false;
  texture.premultiplyAlpha = false;
  texture.minFilter = filter === 'nearest' ? THREE.NearestFilter : THREE.LinearFilter;
  texture.magFilter = filter === 'nearest' ? THREE.NearestFilter : THREE.LinearFilter;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return texture;
}

export function colorTexture(bitmap: ImageBitmap, anisotropy: number): THREE.Texture {
  const texture = new THREE.Texture(bitmap);
  texture.flipY = false;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.anisotropy = anisotropy;
  texture.needsUpdate = true;
  return texture;
}

export function spriteTexture(bitmap: ImageBitmap): THREE.Texture {
  const texture = new THREE.Texture(bitmap);
  texture.flipY = false;
  texture.colorSpace = THREE.NoColorSpace;
  // the bitmap is already premultiplied by createImageBitmap
  texture.premultiplyAlpha = false;
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.needsUpdate = true;
  return texture;
}

/** Reads the pixels of a bitmap on the CPU (used for zone picking). */
export function bitmapPixels(bitmap: ImageBitmap): Uint8ClampedArray {
  const canvas =
    typeof OffscreenCanvas !== 'undefined'
      ? new OffscreenCanvas(bitmap.width, bitmap.height)
      : Object.assign(document.createElement('canvas'), {
          width: bitmap.width,
          height: bitmap.height,
        });
  const ctx = canvas.getContext('2d', { willReadFrequently: true, colorSpace: 'srgb' }) as
    OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D | null;
  if (!ctx) throw new Error('2D canvas is not available');
  ctx.drawImage(bitmap, 0, 0);
  return ctx.getImageData(0, 0, bitmap.width, bitmap.height).data;
}

interface CacheEntry {
  texture: THREE.Texture;
  bytes: number;
  lastUsed: number;
}

/**
 * LRU cache of material textures with a GPU memory budget. Textures in use by the current
 * configuration are pinned and never evicted.
 */
export class TextureCache {
  private entries = new Map<string, CacheEntry>();
  private pending = new Map<string, Promise<THREE.Texture>>();
  private usedBytes = 0;

  constructor(
    private readonly budgetBytes: number,
    private readonly anisotropy: () => number,
  ) {}

  has(url: string): boolean {
    return this.entries.has(url);
  }

  async get(url: string, pinned: ReadonlySet<string>): Promise<THREE.Texture> {
    const hit = this.entries.get(url);
    if (hit) {
      hit.lastUsed = performance.now();
      return hit.texture;
    }
    let promise = this.pending.get(url);
    if (!promise) {
      promise = loadBitmap(url, 'color').then((bitmap) => {
        const texture = colorTexture(bitmap, this.anisotropy());
        // RGBA8 + mip chain ≈ 4/3 of the base level
        const bytes = Math.round(bitmap.width * bitmap.height * 4 * 1.34);
        this.entries.set(url, { texture, bytes, lastUsed: performance.now() });
        this.usedBytes += bytes;
        this.evict(pinned);
        return texture;
      });
      this.pending.set(url, promise);
      promise.finally(() => this.pending.delete(url)).catch(() => undefined);
    }
    return promise;
  }

  private evict(pinned: ReadonlySet<string>) {
    if (this.usedBytes <= this.budgetBytes) return;
    const candidates = [...this.entries.entries()]
      .filter(([url]) => !pinned.has(url))
      .sort((a, b) => a[1].lastUsed - b[1].lastUsed);
    for (const [url, entry] of candidates) {
      if (this.usedBytes <= this.budgetBytes) break;
      entry.texture.dispose();
      const image = entry.texture.image as ImageBitmap | undefined;
      image?.close?.();
      this.entries.delete(url);
      this.usedBytes -= entry.bytes;
    }
  }

  /**
   * Replaces every cached texture with a copy that shares its decoded image (after a WebGL
   * context restore, when the old GPU objects are gone). Returns old → new.
   */
  renew(): Map<THREE.Texture, THREE.Texture> {
    const remap = new Map<THREE.Texture, THREE.Texture>();
    for (const entry of this.entries.values()) {
      const copy = entry.texture.clone();
      remap.set(entry.texture, copy);
      entry.texture = copy;
    }
    return remap;
  }

  dispose() {
    for (const entry of this.entries.values()) {
      entry.texture.dispose();
      (entry.texture.image as ImageBitmap | undefined)?.close?.();
    }
    this.entries.clear();
    this.usedBytes = 0;
  }
}
